import type { ReactNode } from 'react'
import { useEffect } from 'react'
import { createContext, useContext, useState } from 'react'
import { createPortal } from 'react-dom'
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react-dom'
import { QuestionMarkCircleIcon } from '@heroicons/react/16/solid'
import { useDialogPortal } from './DialogPortalContext.js'

interface TooltipContextType {
  showTooltip: (content: ReactNode, event: MouseEvent) => void
  hideTooltip: () => void
}

const TooltipContext = createContext<TooltipContextType | undefined>(undefined)

export const TooltipProvider = ({ children }: { children: ReactNode }) => {
  const [tooltipContent, setTooltipContent] = useState<ReactNode>(null)
  const [coords, setCoords] = useState<{ x: number; y: number }>({ x: 0, y: 0 })
  const { dialogRef } = useDialogPortal()

  const { refs, floatingStyles } = useFloating({
    placement: 'top',
    middleware: [offset(8), shift(), flip()],
    whileElementsMounted: autoUpdate
  })

  useEffect(() => {
    setTooltipContent(null)
  }, [dialogRef, dialogRef?.current])

  const showTooltip = (content: ReactNode, event: MouseEvent) => {
    setTooltipContent(content)
    setCoords({ x: event.clientX, y: event.clientY })
  }

  const hideTooltip = () => {
    setTooltipContent(null)
  }

  return (
    <TooltipContext.Provider value={{ showTooltip, hideTooltip }}>
      {children}
      {createPortal(
        tooltipContent && (
          <div
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              position: 'fixed',
              top: coords.y + 12,
              left: coords.x + 12,
              zIndex: 9999,
              pointerEvents: 'none',
              backgroundColor: 'rgba(0,0,0,0.75)',
              color: 'white',
              padding: '6px 8px',
              borderRadius: '4px',
              fontSize: '0.75rem',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              maxWidth: '300px',
              transform: 'translate3d(0, 0, 0)'
            }}
          >
            {tooltipContent}
          </div>
        ),
        (dialogRef?.current ?? document.body) as HTMLElement
      )}
    </TooltipContext.Provider>
  )
}

export const useTooltip = () => {
  const ctx = useContext(TooltipContext)
  if (!ctx) throw new Error('useTooltip must be inside TooltipProvider')
  return ctx
}

interface TooltipTriggerProps {
  children: ReactNode
  tooltip: ReactNode
  disabledTooltip?: ReactNode
  disabled?: boolean
  withIcon?: boolean
  classNames?: string
}

export const TooltipTrigger = (props: TooltipTriggerProps) => {
  const { showTooltip, hideTooltip } = useTooltip()

  if (props.disabled === true && !props.disabledTooltip) {
    return <div className={`${props.classNames ? props.classNames : 'inline-block'}`}>{props.children}</div>
  }
  return (
    <div
      onMouseEnter={(e) => showTooltip(props.disabled ? props.disabledTooltip : props.tooltip, e.nativeEvent)}
      onMouseMove={(e) => showTooltip(props.disabled ? props.disabledTooltip : props.tooltip, e.nativeEvent)}
      onMouseLeave={hideTooltip}
      className={`${props.classNames ? props.classNames : 'inline-block'}`}
    >
      {props.children}
      {props.withIcon && <QuestionMarkCircleIcon className="align-middle pl-2 w-[1em] h-[1em] inline-block" />}
    </div>
  )
}
